import { Injectable } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Action, State, StateContext } from "@ngxs/store";
import produce from "immer";
import { delay, Observable, of, repeat, tap } from "rxjs";

import { NewsPage } from "../models/news-page";
import { NewsStatus } from "../models/news-status.enum";
import { News } from "../models/news.model";
import { SetNewsStatusCommand } from "../models/set-news-status-command.model";
import { NewsService } from "../services/news.service";
import { NewsActions } from "./news.actions";

export interface NewsStateModel {
    news: News[] | undefined;
    unreadNews: News[];
    unseenNewsCount: number;
    currentNews: NewsPage | undefined;
    loading: boolean | undefined;
}

const defaults = {
    news: undefined,
    unreadNews: [],
    unseenNewsCount: 0,
    currentNews: undefined,
    loading: undefined,
};

@UntilDestroy()
@Injectable()
@State<NewsStateModel>({
    name: "news",
    defaults: defaults,
})
export class NewsState {
    constructor(private newsService: NewsService) {}

    public ngxsOnInit(ctx: StateContext<NewsStateModel>): void {
        of(null).pipe(delay(60 * 1000), repeat(), untilDestroyed(this)).subscribe(() => {
            ctx.dispatch(new NewsActions.GetUnseenNewsCount());
        });
    }

    @Action(NewsActions.GetAllNews)
    public getAll(ctx: StateContext<NewsStateModel>): Observable<News[]> {
        return this.newsService.getAll().pipe(tap((news) => {
            if (news) {
                ctx.patchState({ news: news });
            }

            // Update count
            ctx.dispatch(new NewsActions.GetUnseenNewsCount());
        }));
    }

    @Action(NewsActions.GetUnreadNews)
    public getUnreadNews(ctx: StateContext<NewsStateModel>): Observable<News[]> {
        ctx.setState(produce((draft) => {
            draft.loading = true;
        }));

        return this.newsService.getUnreadNews().pipe(tap((news) => {
            ctx.patchState({ unreadNews: news, loading: false });

            // Update count
            ctx.dispatch(new NewsActions.GetUnseenNewsCount());
        }));
    }

    @Action(NewsActions.GetUnseenNewsCount)
    public getUnseenNewsCount(ctx: StateContext<NewsStateModel>): Observable<number> {
        return this.newsService.getUnseenNewsCount().pipe(tap((count) => {
            ctx.patchState({ unseenNewsCount: count });
        }));
    }

    @Action(NewsActions.GetCurrentNews)
    public getCurrentNews(ctx: StateContext<NewsStateModel>, action: NewsActions.GetCurrentNews): Observable<NewsPage> {
        ctx.patchState({ currentNews: undefined });

        return this.newsService.getNewsPage(action.newsId).pipe(tap((newsPage) => {
            ctx.patchState({
                currentNews: newsPage,
            });
        }));
    }

    @Action(NewsActions.SetNewsStatus)
    public setNewsStatus(ctx: StateContext<NewsStateModel>, action: NewsActions.SetNewsStatus): Observable<boolean> {
        const command: SetNewsStatusCommand = {
            newsIds: [action.newsId],
            status: action.status,
        };

        return this.setStatusForNews(ctx, command);
    }

    @Action(NewsActions.SetStatusForAll)
    public setStatusForAll(
        ctx: StateContext<NewsStateModel>,
        action: NewsActions.SetStatusForAll,
    ): Observable<boolean> {
        const state = ctx.getState();

        const command: SetNewsStatusCommand = {
            newsIds: state.news?.map((_) => _.contentId) ?? [],
            status: action.status,
        };

        return this.setStatusForNews(ctx, command);
    }

    @Action(NewsActions.SetStatusForAllUnread)
    public setStatusForAllUnread(
        ctx: StateContext<NewsStateModel>,
        action: NewsActions.SetStatusForAllUnread,
    ): Observable<boolean> {
        const state = ctx.getState();

        const command: SetNewsStatusCommand = {
            newsIds: state.unreadNews.map((_) => _.contentId),
            status: action.status,
        };

        return this.setStatusForNews(ctx, command);
    }

    @Action(NewsActions.RemoveNews)
    public removeNews(ctx: StateContext<NewsStateModel>, action: NewsActions.RemoveNews): Observable<boolean> {
        const command: SetNewsStatusCommand = {
            newsIds: [action.newsId],
            status: NewsStatus.Deleted,
        };

        return this.setStatusForNews(ctx, command);
    }

    private setStatusForNews(ctx: StateContext<NewsStateModel>, command: SetNewsStatusCommand): Observable<boolean> {
        return this.newsService.setStatusForNews(command).pipe(tap((result) => {
            if (result) {
                ctx.dispatch(new NewsActions.GetAllNews());
            }
        }));
    }
}
